import { useReducer } from "react";
import { Entity, expectNever } from "../../lib";

type EventAdd<T> = {
  type: "add";
  item: T;
};
type EventRemove = {
  type: "remove";
  ids: string[];
};
type EventSet<T> = {
  type: "set";
  items: T[];
};
type EventReducer<T> = EventAdd<T> | EventRemove | EventSet<T>;

export const useRepository = <T extends Entity>() => {
  const [items, updateItems] = useReducer(
    (items: T[], event: EventReducer<T>) => {
      const { type } = event;
      switch (type) {
        case "add": {
          const { item } = event;
          return [item, ...items];
        }
        case "remove": {
          const { ids } = event;
          if (!ids.length) return items;
          return items.filter((notification) => !ids.includes(notification.id));
        }
        case "set": {
          return event.items;
        }
        default: {
          expectNever(type);
          throw new Error();
        }
      }
    },
    []
  );

  const add = (item: T) => {
    updateItems({
      type: "add",
      item,
    });
  };
  const remove = (ids: string[]) => {
    updateItems({
      type: "remove",
      ids,
    });
  };
  const setItems = (items: T[]) => {
    updateItems({
      type: "set",
      items,
    });
  };
  return { items, add, remove, setItems };
};
